import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Download, ExternalLink, AlertTriangle, Eye, ThumbsUp, ThumbsDown } from 'lucide-react';
import ModulLayout from '../layout/ModulLayout';
import { PANEL_SIRKA } from '../layout/ModulLayout';
import ListToolbar, { PohledPill } from '../layout/ListToolbar';
import { FiltrTlacitko, AktivniFiltry } from '../layout/ListFiltr';
import KDoreseniRadek, { KDoreseniSeznam } from '../layout/KDoreseni';
import ObjednavkaDetail from './ObjednavkaDetail';
import UctenkaObjednavky from './UctenkaObjednavky';
import ObdobiVolba from './ObdobiVolba';
import {
    SECTION_COLOR, SECTION_BG, KANALY, fmtKc, fmtCas, fmtDen, fmtDatum, platbaPopis, StavOdznak, Nadpis,
    RadekPanelu, Prubeh, stavOd, minut, fmtMinut, jePoSlibu, kDoreseniObjednavek,
    KpiPruh, DoruceniBlok, barvaSlibu, minutRozvozu, LIMIT_60, CERVENA,
} from './ui';
import { popisObjednavky, jeHotova } from './stavy';

/**
 * Hosté → Objednávky. Norma §1: seznam · detail · třetí sloupec
 * (`panelVzdy` — panel odpovídá na otázku „jak jde den / období" bez ohledu
 * na výběr, jako účet v Bance).
 *
 * Jedna obrazovka, dva režimy podle zvoleného období (`ObdobiVolba` vedle
 * hledání). Do 2026-10-06 to byly dvě záložky, Živě a Historie.
 *  - Dnes (živě): celý dnešek najednou, seskupený na Běží · Hotové · Storna,
 *    pohledy podle kanálu. Obnovuje se přes WebSocket `objednavky.live`
 *    (scraper a POS vysílají při každé změně), záložně jednou za minutu.
 *  - Jiné období (historie): filtry na serveru, seskupení po dnech, po
 *    stránkách, hledání i podle telefonu a adresy, export CSV.
 * Bez přístupu `objednavky.historie` se nabízí jen Dnes a Včera (server
 * období stejně ořízne) a export chybí.
 *
 * „K dořešení" počítá `kDoreseniObjednavek()` — tentýž seznam kreslí panel
 * i mobilní řádek (§1g).
 */

// `kratce` = popisek na tlačítku, `jenDen` = smí i bez přístupu k historii
const OBDOBI = [
    { value: 'dnes', label: 'Dnes', jenDen: true },
    { value: 'vcera', label: 'Včera', jenDen: true },
    { value: '7', label: 'Posledních 7 dní', kratce: '7 dní' },
    { value: '30', label: 'Posledních 30 dní', kratce: '30 dní' },
    { value: 'mesic', label: 'Tento měsíc' },
    { value: 'minuly', label: 'Minulý měsíc' },
    { value: '90', label: 'Posledních 90 dní', kratce: '90 dní' },
    { value: '365', label: 'Posledních 12 měsíců', kratce: '12 měsíců' },
    { value: 'vlastni', label: 'Vlastní období…' },
];
// `od`/`do` platí jen s obdobím „vlastni" (Y-m-d)
const VYCHOZI_FILTR = { obdobi: 'dnes', od: null, do: null, kanal: [], platba: [], zdroj: [] };

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
function rozsah(obdobi, vlastniOd = null, vlastniDo = null) {
    const dnes = new Date();
    const do_ = iso(dnes);
    if (obdobi === 'vlastni') {
        // Dokud není vyplněný jeden z konců, platí druhý (nebo dnešek)
        const od = vlastniOd || vlastniDo || do_;
        const d = vlastniDo || vlastniOd || do_;
        return od <= d ? [od, d] : [d, od];
    }
    if (obdobi === 'dnes') return [do_, do_];
    if (obdobi === 'vcera') {
        const v = iso(new Date(dnes.getFullYear(), dnes.getMonth(), dnes.getDate() - 1));
        return [v, v];
    }
    if (obdobi === 'mesic') return [iso(new Date(dnes.getFullYear(), dnes.getMonth(), 1)), do_];
    if (obdobi === 'minuly') return [iso(new Date(dnes.getFullYear(), dnes.getMonth() - 1, 1)), iso(new Date(dnes.getFullYear(), dnes.getMonth(), 0))];
    const n = Number(obdobi) || 7;
    return [iso(new Date(dnes.getFullYear(), dnes.getMonth(), dnes.getDate() - (n - 1))), do_];
}

const POHLEDY_ZIVE = [
    { id: 'vse', label: 'Vše', patri: () => true },
    { id: 'rozvoz', label: 'Rozvoz', patri: o => o.kanal === 'rozvoz' },
    { id: 'bistro', label: 'Bistro', patri: o => o.kanal === 'bistro' },
    { id: 'vydej', label: 'Výdej', patri: o => o.kanal === 'vydej' },
];
const POHLEDY_HISTORIE = [
    { id: 'platne', label: 'Platné', kategorie: 'hotova,rozpracovana' },
    { id: 'storno', label: 'Storna', kategorie: 'storno' },
    { id: 'nezaplacene', label: 'Nezaplacené', kategorie: 'ceka_platba,otevrena' },
    { id: 'odlozene', label: 'Odložené', kategorie: 'odlozena' },
    { id: 'vse', label: 'Vše', kategorie: '' },
];
const SKUPINY_ZIVE = [
    { id: 'bezi', nazev: 'Běží', patri: o => ['rozpracovana', 'ceka_platba', 'otevrena'].includes(o.kategorie) || (o.kategorie === 'hotova' && !jeHotova(o)) },
    // Objednávky na čas — jen naplánované, do čísel dne se nepočítají, dokud je Objedname nepotvrdí
    { id: 'odlozene', nazev: 'Odložené', patri: o => o.kategorie === 'odlozena' },
    { id: 'hotove', nazev: 'Hotové', patri: o => o.kategorie === 'hotova' && jeHotova(o) },
    { id: 'storna', nazev: 'Storna a nahrazené', patri: o => ['storno', 'nahrazena'].includes(o.kategorie) },
];

/** Které sloupce přehledu se vejdou (norma §3: odebírají se v pevném pořadí). */
function useSloupce(rezim) {
    const [sirka, setSirka] = useState(() => window.innerWidth);
    useEffect(() => {
        const h = () => setSirka(window.innerWidth);
        window.addEventListener('resize', h);
        return () => window.removeEventListener('resize', h);
    }, []);
    return {
        rozvozce: sirka >= 1500,
        platba: sirka >= 1360,
        doruceni: rezim === 'zive' || sirka >= 1220,
    };
}

/**
 * @param {boolean} [props.muzeHistorie] - přístup `objednavky.historie`: delší období a export
 * @param {string} [props.vychoziObdobi] - s čím se seznam otevře (`dnes`, `7`…)
 * @param {(stav: {zive: boolean, kompaktni: boolean, den: ?string}) => React.ReactNode} [props.panelNavic]
 *        blok na konec třetího sloupce; na mobilu (`kompaktni`) pod čísla nad
 *        seznamem — dnešní rezervace a přehled provozního dne (`den` = vybraný
 *        jediný den, jinak null)
 */
export default function ObjednavkySeznam({ isDark, isMobile, cardStyle, inputStyle, mutedColor, rezervaDole = null, otevritId = null, onZakaznik, muzeHistorie = false, vychoziObdobi = 'dnes', panelNavic = null }) {
    const volbyObdobi = useMemo(() => OBDOBI.filter(o => muzeHistorie || o.jenDen), [muzeHistorie]);
    const [filtr, setFiltr] = useState(() => ({
        ...VYCHOZI_FILTR,
        obdobi: OBDOBI.some(o => o.value === vychoziObdobi && (muzeHistorie || o.jenDen)) ? vychoziObdobi : 'dnes',
    }));
    const zive = filtr.obdobi === 'dnes';
    // Vybraný jediný den (Dnes, Včera, vlastní den) — panel provozního dne.
    const jedenDen = (() => {
        const [od, do_] = rozsah(filtr.obdobi, filtr.od, filtr.do);
        return od === do_ ? od : null;
    })();
    const rezim = zive ? 'zive' : 'historie';
    const [radky, setRadky] = useState(null);
    const [meta, setMeta] = useState(null);
    const [stats, setStats] = useState(null);
    const [chyba, setChyba] = useState(null);
    const [vybranyId, setVybranyId] = useState(otevritId ? Number(otevritId) : null);
    const [hledani, setHledani] = useState('');
    const [hledaniServer, setHledaniServer] = useState('');
    const [pohled, setPohled] = useState(zive ? 'vse' : 'platne');
    const [strana, setStrana] = useState(1);
    const [now, setNow] = useState(Date.now());
    // Echo vzniká v app.jsx až po přihlášení — stránka otevřená dřív (obnovení,
    // proklik z notifikace) by se jinak ke kanálu nepřipojila nikdy.
    const [echoPripraveno, setEchoPripraveno] = useState(() => !!window.Echo);
    useEffect(() => {
        if (echoPripraveno) return undefined;
        const naEcho = () => setEchoPripraveno(!!window.Echo);
        window.addEventListener('echo-pripraveno', naEcho);
        const pojistka = setInterval(naEcho, 1000);
        return () => { window.removeEventListener('echo-pripraveno', naEcho); clearInterval(pojistka); };
    }, [echoPripraveno]);
    // Otevřený detail se při živé změně načte znovu (bez blikání načítání)
    const [obnovaDetailu, setObnovaDetailu] = useState(0);
    const sloupce = useSloupce(rezim);

    useEffect(() => { if (otevritId) setVybranyId(Number(otevritId)); }, [otevritId]);

    // Hledání v Historii jde na server — s prodlevou, ať se neposílá každé písmeno
    useEffect(() => {
        if (zive) return undefined;
        const t = setTimeout(() => setHledaniServer(hledani.trim()), 350);
        return () => clearTimeout(t);
    }, [hledani, zive]);

    const parametry = useCallback((sKategorii = true) => {
        const p = new URLSearchParams();
        if (zive) { p.set('rezim', 'zive'); return p; }
        const [od, do_] = rozsah(filtr.obdobi, filtr.od, filtr.do);
        p.set('od', od); p.set('do', do_);
        if (filtr.kanal.length) p.set('kanal', filtr.kanal.join(','));
        if (filtr.platba.length) p.set('platba', filtr.platba.join(','));
        if (filtr.zdroj.length) p.set('zdroj', filtr.zdroj.join(','));
        if (hledaniServer) p.set('q', hledaniServer);
        const kat = POHLEDY_HISTORIE.find(x => x.id === pohled)?.kategorie;
        if (sKategorii && kat) p.set('kategorie', kat);
        return p;
    }, [zive, filtr, hledaniServer, pohled]);

    const nactiSeznam = useCallback(async (dalsiStrana = 1) => {
        const p = parametry(true);
        if (!zive) p.set('page', String(dalsiStrana));
        try {
            const res = await fetch(`/api/objednavky?${p}`, { headers: { Accept: 'application/json' } });
            if (!res.ok) throw new Error(res.status === 403 ? 'Nemáte přístup.' : 'Objednávky se nepodařilo načíst.');
            const d = await res.json();
            setChyba(null);
            setMeta(d.meta);
            setStrana(dalsiStrana);
            setRadky(prev => (dalsiStrana > 1 && prev ? [...prev, ...d.data] : d.data));
        } catch (e) {
            setChyba(e.message);
            if (dalsiStrana === 1) setRadky([]);
        }
    }, [parametry, zive]);

    const nactiStats = useCallback(async () => {
        try {
            const res = await fetch(`/api/objednavky/stats?${parametry(false)}`, { headers: { Accept: 'application/json' } });
            if (res.ok) setStats(await res.json());
        } catch { /* panel počká na další obnovu */ }
    }, [parametry]);

    const obnov = useCallback(() => { nactiSeznam(1); nactiStats(); }, [nactiSeznam, nactiStats]);
    useEffect(() => { setRadky(null); obnov(); }, [obnov]);

    // Živě: WebSocket + záložní obnova a tikání časovačů
    const odlozena = useRef(null);
    useEffect(() => {
        if (!zive) return undefined;
        const naZmenu = () => {
            clearTimeout(odlozena.current);
            odlozena.current = setTimeout(() => { obnov(); setObnovaDetailu(n => n + 1); }, 800);
        };
        let kanal = null;
        try { kanal = echoPripraveno ? window.Echo?.private('objednavky.live') : null; } catch { kanal = null; }
        kanal?.listen('.NovaObjednavka', naZmenu).listen('.ObjednavkaAktualizovana', naZmenu);
        // Telefon v pozadí WebSocket uspí — po návratu dorovnat hned, ne až za minutu
        const naViditelnost = () => { if (!document.hidden) naZmenu(); };
        document.addEventListener('visibilitychange', naViditelnost);
        const zaloha = setInterval(obnov, 60000);
        const tik = setInterval(() => setNow(Date.now()), 30000);
        return () => {
            // Jen vlastní posluchače — `Echo.leave` by odpojil i jiné okno
            kanal?.stopListening('.NovaObjednavka', naZmenu).stopListening('.ObjednavkaAktualizovana', naZmenu);
            document.removeEventListener('visibilitychange', naViditelnost);
            clearInterval(zaloha); clearInterval(tik); clearTimeout(odlozena.current);
        };
    }, [zive, obnov, echoPripraveno]);

    // ── Data pro seznam ──
    const vse = radky || [];
    const hledat = hledani.trim().toLowerCase();
    const pohledZive = POHLEDY_ZIVE.find(p => p.id === pohled) || POHLEDY_ZIVE[0];
    const viditelne = useMemo(() => {
        if (!zive) return vse;
        const cislice = hledat.replace(/\D/g, '');
        return vse.filter(o => pohledZive.patri(o) && (!hledat
            || `${o.cislo_objednavky} ${o.cislo_denni} ${o.zakaznik?.jmeno || ''} ${o.adresa_doruceni || ''} ${o.pos_nazev_uctu || ''}`.toLowerCase().includes(hledat)
            || (cislice.length >= 3 && String(o.zakaznik?.telefon || '').replace(/\D/g, '').includes(cislice))));
    }, [vse, zive, hledat, pohledZive]);

    const skupiny = useMemo(() => {
        if (zive) {
            return SKUPINY_ZIVE.map(s => {
                const polozky = viditelne.filter(s.patri);
                return { klic: s.id, nazev: s.nazev, polozky, soucet: polozky.reduce((a, o) => a + Number(o.celkem_s_dph || 0), 0) };
            }).filter(s => s.polozky.length);
        }
        const m = new Map();
        for (const o of viditelne) {
            if (!m.has(o.datum)) m.set(o.datum, { klic: o.datum, nazev: fmtDen(o.datum), polozky: [], soucet: 0 });
            const g = m.get(o.datum);
            g.polozky.push(o);
            g.soucet += Number(o.celkem_s_dph || 0);
        }
        return [...m.values()];
    }, [viditelne, zive]);

    const kDoreseni = useMemo(() => (zive ? kDoreseniObjednavek(vse, now) : []), [vse, now, zive]);
    const poctyZive = useMemo(() => Object.fromEntries(POHLEDY_ZIVE.map(p => [p.id, vse.filter(p.patri).length])), [vse]);
    const poctyHistorie = stats ? {
        platne: stats.pocet,
        storno: stats.storno,
        nezaplacene: stats.ceka_platba + stats.otevrene,
        odlozene: stats.odlozene || 0,
        vse: stats.pocet + stats.storno + stats.ceka_platba + stats.otevrene + (stats.nahrazene || 0) + (stats.odlozene || 0),
    } : {};

    const cara = isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-subtle)';
    const muted = mutedColor;

    // ── Období ──
    const zmenObdobi = (obdobi, od = null, do_ = null) => {
        const budeZive = obdobi === 'dnes';
        // Pohledy se mezi režimy liší (kanály × stavy) — přepnutí začne od výchozího
        if (budeZive !== zive) setPohled(budeZive ? 'vse' : 'platne');
        setFiltr(f => {
            if (obdobi !== 'vlastni') return { ...f, obdobi, od: null, do: null };
            // Vlastní období začne od toho, co bylo vidět
            const [pOd, pDo] = f.obdobi === 'vlastni' ? [f.od, f.do] : rozsah(f.obdobi);
            return { ...f, obdobi, od: od ?? pOd, do: do_ ?? pDo };
        });
    };
    const popisVlastni = filtr.obdobi === 'vlastni'
        ? (() => { const [od, d] = rozsah('vlastni', filtr.od, filtr.do); return od === d ? fmtDatum(od) : `${fmtDatum(od)} – ${fmtDatum(d)}`; })()
        : null;

    // ── Filtr (jen mimo Dnes) ──
    const filtrSkupiny = useMemo(() => [
        { id: 'kanal', typ: 'volby', label: 'Kanál', volby: Object.entries(KANALY).map(([value, label]) => ({ value, label })) },
        { id: 'platba', typ: 'volby', label: 'Platba', volby: [{ value: 'hotove', label: 'Hotově' }, { value: 'karta', label: 'Kartou' }, { value: 'online', label: 'Online' }] },
        { id: 'zdroj', typ: 'volby', label: 'Zdroj', volby: [{ value: 'objedname', label: 'Objedname' }, { value: 'pos', label: 'POS' }] },
    ], []);
    const nastavFiltr = (id, hodnota) => setFiltr(f => ({ ...f, [id]: hodnota ?? VYCHOZI_FILTR[id] }));
    // Období není filtr — zrušení filtrů ho nechá být
    const resetFiltru = () => setFiltr(f => ({ ...VYCHOZI_FILTR, obdobi: f.obdobi, od: f.od, do: f.do }));

    const exportovat = () => {
        const p = parametry(true);
        window.location.href = `/api/objednavky/export?${p}`;
    };

    // ── Řádek ──
    const grid = useMemo(() => {
        const s = [zive ? '58px' : '64px', zive ? '52px' : '58px', 'minmax(180px, 1fr)', '74px'];
        if (sloupce.platba) s.push('78px');
        if (sloupce.doruceni) s.push('104px');
        s.push('150px');
        if (sloupce.rozvozce) s.push('130px');
        s.push('92px');
        return { display: 'grid', gridTemplateColumns: s.join(' '), columnGap: 12, alignItems: 'center' };
    }, [zive, sloupce]);

    /**
     * Doručení rozvozu: doba (červeně přes 60 min — výkon firmy) a rozdíl
     * proti slibu (oranžově v toleranci, červeně po ní — férovost k zákazníkovi).
     * Časovka se do 60 minut neměří, místo doby ukáže slíbený čas.
     */
    const doruceni = (o) => {
        if (o.kanal !== 'rozvoz') return null;
        const v = o.vcasnost;
        const slib = o.cas_doruceni_slibeny?.slice(0, 5);
        if (!v) {
            const bezi = minutRozvozu(o, now);
            if (bezi != null && bezi > LIMIT_60) return <span style={{ color: CERVENA, fontWeight: 600 }}>{bezi} min</span>;
            return slib ? `${o.casovka ? 'na' : 'slib'} ${slib}` : '';
        }
        const r = v.slibeny_rozdil;
        const bSlib = barvaSlibu(v.slibeny_stav);
        return (
            <>
                {v.casovka
                    ? <span title="Časovka — do 60 minut se nepočítá">na {slib}</span>
                    : <span style={v.do60_ok === false ? { color: CERVENA, fontWeight: 600 } : null}>{v.minut_celkem} min</span>}
                {r != null && r > 0 && <span style={{ color: bSlib || 'inherit', fontWeight: bSlib ? 600 : 400 }}> · +{r}</span>}
            </>
        );
    };

    const radek = (o, siroky) => {
        const vybrany = o.id === vybranyId;
        const bezi = ['rozpracovana', 'ceka_platba', 'otevrena'].includes(o.kategorie) || (o.kategorie === 'hotova' && !jeHotova(o));
        const vStavu = bezi ? minut(stavOd(o), now) : null;
        // Úzký řádek nemá sloupec Doručení — červeně, když nevyšel slib nebo 60 minut
        const pozde = jePoSlibu(o, now) || o.vcasnost?.slibeny_stav === 'pozde'
            || o.vcasnost?.do60_ok === false || (minutRozvozu(o, now) ?? 0) > LIMIT_60;
        const jmeno = o.zakaznik?.jmeno || o.pos_nazev_uctu || (o.kanal === 'bistro' ? 'Bistro' : 'bez jména');
        const cislo = o.cislo_denni ? `#${o.cislo_denni}` : `#${String(o.cislo_objednavky || o.id).slice(-5)}`;
        const storno = ['storno', 'nahrazena'].includes(o.kategorie);
        const spolecne = {
            type: 'button', onClick: () => setVybranyId(o.id), className: 'w-full text-left',
            style: {
                border: 'none', borderBottom: `1px solid ${cara}`, cursor: 'pointer',
                background: vybrany ? SECTION_BG : 'transparent', boxShadow: vybrany ? `inset 3px 0 0 ${SECTION_COLOR}` : 'none',
                opacity: storno ? 0.62 : 1,
            },
        };

        if (siroky) {
            return (
                <button key={o.id} {...spolecne} style={{ ...spolecne.style, ...grid, padding: '0 14px', minHeight: 44 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>{cislo}</span>
                    <span style={{ fontSize: 12.5, color: muted, fontVariantNumeric: 'tabular-nums' }}>{fmtCas(o.cas)}</span>
                    <span className="min-w-0" style={{ fontSize: 13 }}>
                        <span className="flex items-center min-w-0" style={{ gap: 6 }}>
                            <span className="truncate" style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{jmeno}</span>
                            <IkonyOdkazu odkaz={o.odkaz} />
                        </span>
                        {(o.zakaznik?.telefon || o.adresa_doruceni) && (
                            <span className="truncate" style={{ display: 'block', fontSize: 11.5, color: muted }}>
                                {[o.zakaznik?.telefon, o.kanal === 'rozvoz' ? o.adresa_doruceni : null].filter(Boolean).join(' · ')}
                            </span>
                        )}
                    </span>
                    <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{KANALY[o.kanal] || o.kanal}</span>
                    {sloupce.platba && <span className="truncate" style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{platbaPopis(o.typ_platby)}</span>}
                    {sloupce.doruceni && <span className="truncate" style={{ fontSize: 12.5, color: jePoSlibu(o, now) ? '#F87171' : 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>{doruceni(o)}</span>}
                    <span className="flex items-center min-w-0" style={{ gap: 6 }}>
                        <StavOdznak o={o} isDark={isDark} maly />
                        {vStavu != null && <span style={{ fontSize: 11.5, color: muted, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{fmtMinut(vStavu)}</span>}
                    </span>
                    {sloupce.rozvozce && <span className="truncate" style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{o.rozvozce || ''}</span>}
                    <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-primary)', textAlign: 'right', fontVariantNumeric: 'tabular-nums', textDecoration: storno ? 'line-through' : 'none' }}>{fmtKc(o.celkem_s_dph)}</span>
                </button>
            );
        }

        const meta = [
            KANALY[o.kanal] || o.kanal,
            zive ? fmtCas(o.cas) : `${fmtDen(o.datum)} ${fmtCas(o.cas)}`,
            jeHotova(o) ? null : popisObjednavky(o) + (vStavu != null ? ` ${fmtMinut(vStavu)}` : ''),
            o.vcasnost ? (o.vcasnost.casovka ? `časovka ${o.cas_doruceni_slibeny?.slice(0, 5) || ''}`.trim() : `za ${o.vcasnost.minut_celkem} min`) : null,
            o.rozvozce,
        ].filter(Boolean).join(' · ');
        return (
            <button key={o.id} {...spolecne} style={{ ...spolecne.style, display: 'flex', alignItems: 'center', gap: 12, padding: isMobile ? '10px 16px' : '9px 14px', minHeight: isMobile ? 60 : 54 }}>
                <div className="flex-1 min-w-0">
                    <p className="truncate" style={{ margin: 0, fontSize: isMobile ? 15 : 13.5, color: 'var(--text-primary)' }}>
                        <b style={{ fontVariantNumeric: 'tabular-nums' }}>{cislo}</b> <span style={{ fontWeight: 500 }}>{jmeno}</span> <IkonyOdkazu odkaz={o.odkaz} inline />
                    </p>
                    <p className="truncate" style={{ margin: '2px 0 0', fontSize: isMobile ? 12.5 : 11.5, color: pozde ? '#F87171' : muted }}>
                        {pozde && <AlertTriangle size={11} style={{ display: 'inline', verticalAlign: '-1px', marginRight: 4 }} />}{meta}
                    </p>
                </div>
                <div className="flex flex-col items-end" style={{ gap: 3, flexShrink: 0 }}>
                    <span style={{ fontSize: isMobile ? 14.5 : 13.5, fontWeight: 700, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums', textDecoration: storno ? 'line-through' : 'none' }}>{fmtKc(o.celkem_s_dph)}</span>
                    {storno && <StavOdznak o={o} isDark={isDark} maly />}
                </div>
            </button>
        );
    };

    // ── Panel (třetí sloupec) ──
    const souhrnBloky = (kompaktni = false) => {
        if (!stats) return <p className="text-xs" style={{ color: muted }}>Načítám…</p>;
        const zmena = stats.predchozi?.trzby ? Math.round(((stats.trzby - stats.predchozi.trzby) / stats.predchozi.trzby) * 100) : null;
        const kanaly = Object.entries(KANALY).map(([k, label]) => ({ k, label, ...(stats.kanaly?.[k] || { pocet: 0, trzby: 0 }) }));
        const maxKanal = Math.max(1, ...kanaly.map(k => k.trzby));
        return (
            <>
                <Nadpis muted={muted}>{zive ? 'Dnes' : stats.od === stats.do ? fmtDatum(stats.od) : `${fmtDatum(stats.od)} – ${fmtDatum(stats.do)}`}</Nadpis>
                <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>{fmtKc(stats.trzby)}</div>
                <p style={{ margin: '2px 0 8px', fontSize: 12, color: muted }}>
                    {zmena != null ? `${zmena >= 0 ? '+' : '−'}${Math.abs(zmena)} % proti ${zive ? 'stejnému dni minulý týden' : 'předchozímu období'}` : 'bez srovnání'}
                </p>
                {!kompaktni && stats.prubeh?.body?.length > 1 && (
                    <div style={{ margin: '2px 0 10px' }}>
                        <Prubeh body={stats.prubeh.body.map(b => b.t)} />
                        <p style={{ margin: '3px 0 0', fontSize: 11, color: muted }}>{stats.prubeh.po === 'hodiny' ? 'tržby po hodinách' : 'tržby po dnech'}</p>
                    </div>
                )}
                <RadekPanelu popis="Objednávek" hodnota={stats.pocet} muted={muted} cara={cara} />
                <RadekPanelu popis="Průměrná objednávka" hodnota={fmtKc(stats.prumer)} muted={muted} cara={cara} />
                {stats.body > 0 && <RadekPanelu popis="Uplatněno v bodech" hodnota={fmtKc(stats.body)} muted={muted} cara={cara} />}
                <RadekPanelu popis="Bez zákazníka" hodnota={stats.pocet ? `${Math.round((stats.anonymni / stats.pocet) * 100)} %` : '—'} muted={muted} cara={cara} />
                <RadekPanelu popis="Storna" hodnota={stats.storno ? `${stats.storno} · ${fmtKc(stats.storno_kc)}` : '0'} muted={muted} cara={cara} />
                {!kompaktni && <DoruceniBlok stats={stats} zive={zive} muted={muted} cara={cara} />}
                {stats.odkazy && stats.odkazy.odeslano + stats.odkazy.otevreno > 0 && (
                    // Odkaz ze SMS a žádost o souhlas se SMS (2026-10-23).
                    <>
                        <Nadpis muted={muted} style={{ marginTop: 16 }}>Odkazy ze SMS</Nadpis>
                        <RadekPanelu popis="Odesláno" hodnota={stats.odkazy.odeslano} muted={muted} cara={cara} />
                        <RadekPanelu popis="Otevřeno" hodnota={stats.odkazy.odeslano ? `${stats.odkazy.otevreno} · ${Math.round((stats.odkazy.otevreno / stats.odkazy.odeslano) * 100)} %` : stats.odkazy.otevreno} muted={muted} cara={cara} />
                        <RadekPanelu popis="Žádostí o SMS" hodnota={stats.odkazy.dotazu} muted={muted} cara={cara} />
                        <RadekPanelu popis="Potvrzeno" hodnota={`${stats.odkazy.souhlas}${stats.odkazy.nesouhlas ? ` · odmítli ${stats.odkazy.nesouhlas}` : ''}`} muted={muted} cara={cara} />
                    </>
                )}
                <Nadpis muted={muted} style={{ marginTop: 16 }}>Kanály</Nadpis>
                {kanaly.map(k => (
                    <div key={k.k} style={{ padding: '5px 0' }}>
                        <div className="flex justify-between text-[12.5px]">
                            <span style={{ color: 'var(--text-secondary)' }}>{k.label} <span style={{ color: muted }}>· {k.pocet}</span></span>
                            <b style={{ color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>{fmtKc(k.trzby)}</b>
                        </div>
                        <div style={{ height: 4, borderRadius: 2, marginTop: 4, background: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)' }}>
                            <div style={{ width: `${(k.trzby / maxKanal) * 100}%`, height: '100%', borderRadius: 2, background: SECTION_COLOR }} />
                        </div>
                    </div>
                ))}
            </>
        );
    };

    const otevriAkce = (p) => (p.objednavkaId ? (
        <button type="button" onClick={() => setVybranyId(p.objednavkaId)} className="inline-flex items-center"
            style={{ gap: 5, fontSize: 12, fontWeight: 600, color: SECTION_COLOR, background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', minHeight: 28 }}>
            Otevřít objednávku <ExternalLink size={12} />
        </button>
    ) : null);

    // S vybranou objednávkou patří třetí sloupec její účtence (2026-10-20),
    // bez výběru souhrnu a „K dořešení".
    const panel = () => (vybranyId && !isMobile) ? (
        <div className="rounded-xl" style={{ ...cardStyle, padding: 16, display: 'flex', flexDirection: 'column' }}>
            <UctenkaObjednavky id={vybranyId} sirka={PANEL_SIRKA - 32} obnova={zive ? obnovaDetailu : 0} isDark={isDark} />
        </div>
    ) : (
        <div className="rounded-xl" style={{ ...cardStyle, padding: 16, display: 'flex', flexDirection: 'column' }}>
            {souhrnBloky()}
            {zive && (
                <>
                    <Nadpis muted={muted} style={{ marginTop: 16 }}>K dořešení</Nadpis>
                    <KDoreseniSeznam polozky={kDoreseni} isDark={isDark} renderAkce={otevriAkce} prazdne="Nic nehoří." />
                </>
            )}
            {panelNavic?.({ zive, kompaktni: false, den: jedenDen })}
        </div>
    );

    // ── Seznam ──
    const pohledy = zive
        ? POHLEDY_ZIVE.map(p => ({ id: p.id, label: p.label, pocet: poctyZive[p.id] }))
        : POHLEDY_HISTORIE.map(p => ({ id: p.id, label: p.label, pocet: poctyHistorie[p.id] }));

    const seznamPanel = ({ siroky }) => (
        <div className={isMobile ? 'flex flex-col flex-1 min-h-0' : 'rounded-xl overflow-hidden flex flex-col flex-1 min-h-0'} style={isMobile ? null : cardStyle}>
            <ListToolbar
                siroky={siroky} kompaktni={isMobile} sklo isDark={isDark} mutedColor={muted} inputStyle={inputStyle} barva={SECTION_COLOR}
                hledani={{
                    value: hledani, onChange: setHledani,
                    placeholder: zive ? 'Hledat číslo, jméno, telefon, adresu…' : 'Hledat v období: číslo, jméno, telefon, adresa…',
                    placeholderKratky: 'Hledat',
                }}
                akce={!zive && muzeHistorie && !isMobile ? [{ id: 'export', label: 'Export CSV', Icon: Download, onClick: exportovat }] : []}
                filtr={(
                    <>
                        <ObdobiVolba hodnota={filtr} volby={volbyObdobi} onChange={zmenObdobi} popisVlastni={popisVlastni}
                            poznamka={muzeHistorie ? null : 'Delší období vidí, kdo má přístup k historii objednávek.'}
                            kompaktni={isMobile} isDark={isDark} mutedColor={muted} barva={SECTION_COLOR} />
                        {!zive && (
                            <FiltrTlacitko skupiny={filtrSkupiny} hodnoty={filtr} onChange={nastavFiltr} onReset={resetFiltru}
                                pocetVysledku={meta?.celkem} siroky={siroky} kompaktni={isMobile}
                                isDark={isDark} mutedColor={muted} barva={SECTION_COLOR} />
                        )}
                    </>
                )}
                aktivniFiltry={zive ? null : (
                    <AktivniFiltry skupiny={filtrSkupiny} hodnoty={filtr} onChange={nastavFiltr} onReset={resetFiltru}
                        isDark={isDark} mutedColor={muted} barva={SECTION_COLOR} />
                )}
                pod={isMobile ? (
                    // Telefon nemá třetí sloupec — hlavní čísla jsou pořád nahoře
                    <div className="flex flex-col" style={{ gap: 8 }}>
                        <KpiPruh stats={stats} zive={zive} muted={muted} isDark={isDark} />
                        {zive && (
                            <KDoreseniRadek polozky={kDoreseni} isDark={isDark} barva={SECTION_COLOR} renderAkce={otevriAkce}
                                navic={<div style={{ padding: '14px 16px' }}>{souhrnBloky(true)}</div>} />
                        )}
                        {panelNavic?.({ zive, kompaktni: true, den: jedenDen })}
                    </div>
                ) : null}
                pohledy={!siroky && !isMobile ? pohledy : null}
                pohledAktivni={pohled}
                onPohled={setPohled}
                pohled={!siroky && !isMobile ? null : pohledy.map(p => (
                    <PohledPill key={p.id} label={p.label} pocet={p.pocet ?? null} aktivni={pohled === p.id}
                        onClick={() => setPohled(p.id)} isDark={isDark} mutedColor={muted} barva={SECTION_COLOR} kompaktni={isMobile} />
                ))}
            />

            {siroky && viditelne.length > 0 && (
                <div style={{ ...grid, padding: '9px 14px', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: muted, borderBottom: `1px solid ${cara}`, flexShrink: 0 }}>
                    <span>Číslo</span><span>Čas</span><span>Zákazník</span><span>Kanál</span>
                    {sloupce.platba && <span>Platba</span>}
                    {sloupce.doruceni && <span>Doručení</span>}
                    <span>Stav</span>
                    {sloupce.rozvozce && <span>Rozvozce</span>}
                    <span style={{ textAlign: 'right' }}>Cena</span>
                </div>
            )}

            <div className="flex-1" style={{ overflowY: 'auto', minHeight: 0 }}>
                {chyba && <p className="text-sm text-center" style={{ color: '#F87171', padding: '24px 16px' }}>{chyba}</p>}
                {radky === null && !chyba && <p className="text-center text-sm" style={{ color: muted, padding: 32 }}>Načítám…</p>}
                {radky && !chyba && viditelne.length === 0 && (
                    <p className="text-center text-sm" style={{ color: muted, padding: '32px 16px' }}>
                        {hledat ? 'Nic se neshoduje.' : zive ? 'Dnes zatím žádná objednávka.' : 'V tomhle období nic není.'}
                    </p>
                )}
                {skupiny.map(g => (
                    <div key={g.klic}>
                        <div className="flex justify-between" style={{ padding: '10px 14px 6px', fontSize: 10.5, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: muted, borderBottom: `1px solid ${cara}`, gap: 10 }}>
                            <span className="truncate">{g.nazev}</span>
                            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{g.polozky.length} · {fmtKc(g.soucet)}</span>
                        </div>
                        {g.polozky.map(o => radek(o, siroky))}
                    </div>
                ))}
                {!zive && meta?.dalsi && (
                    <div style={{ padding: 14, display: 'flex', justifyContent: 'center' }}>
                        <button type="button" onClick={() => nactiSeznam(strana + 1)} className="rounded-lg"
                            style={{ minHeight: 40, padding: '0 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', color: 'var(--text-primary)', background: 'transparent', border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}` }}>
                            Načíst další ({vse.length} z {meta.celkem})
                        </button>
                    </div>
                )}
                {rezervaDole && <div aria-hidden="true" style={{ height: rezervaDole }} />}
            </div>
        </div>
    );

    const detailPanel = () => (
        <div className={isMobile ? 'flex flex-col flex-1 min-h-0' : 'rounded-xl overflow-hidden flex flex-col flex-1 min-h-0'} style={isMobile ? null : cardStyle}>
            <ObjednavkaDetail key={vybranyId} id={vybranyId} obnova={zive ? obnovaDetailu : 0} isDark={isDark} isMobile={isMobile} mutedColor={muted}
                onZpet={() => setVybranyId(null)} onZakaznik={onZakaznik}
                onZmena={obnov} onSmazano={() => { setVybranyId(null); obnov(); }} rezervaDole={isMobile ? rezervaDole : null} />
        </div>
    );

    return (
        <div className="flex flex-col" style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <ModulLayout isMobile={isMobile} vybrano={!!vybranyId} mobilRezim mobilPanel={false} panelVzdy barva={SECTION_COLOR} isDark={isDark}
                seznam={seznamPanel} detail={detailPanel} panel={panel} />
        </div>
    );
}


/**
 * Odkaz ze SMS u objednávky (2026-10-23): oko = zákazník stránku objednávky
 * otevřel, palec = na ní souhlasil / nesouhlasil se SMS. Kdo odkaz neotevřel,
 * nemá nic.
 */
function IkonyOdkazu({ odkaz, inline = false }) {
    if (!odkaz) return null;
    const cas = (iso) => (iso ? iso.slice(11, 16) : '');
    return (
        <span className={inline ? 'inline-flex items-center' : 'flex items-center'} style={{ gap: 4, flexShrink: 0, verticalAlign: '-1px' }}>
            <span title={`Otevřel odkaz ze SMS v ${cas(odkaz.otevreno_v)}`} style={{ display: 'inline-flex', color: '#60A5FA' }}>
                <Eye size={13} />
            </span>
            {odkaz.souhlas === true && (
                <span title={`Souhlasí se SMS (${cas(odkaz.souhlas_v)})`} style={{ display: 'inline-flex', color: '#22C55E' }}><ThumbsUp size={13} /></span>
            )}
            {odkaz.souhlas === false && (
                <span title={`Nechce SMS (${cas(odkaz.souhlas_v)})`} style={{ display: 'inline-flex', color: '#F87171' }}><ThumbsDown size={13} /></span>
            )}
        </span>
    );
}
